import { useState, useCallback, useEffect, useMemo, useRef } from "react"
import { useAuth, APIError } from "../../../../../app/modules/auth"
import { FullCircleLead, getAllFullCircleLeads } from "../../../../../mogul/core/_requests"
import { SyncLeadButton } from "../DebugAllLeads"
import { formatLeadItemDateBetter } from "../DebugPipedrive"
import { Link } from "react-router-dom"
import LocationsNeedingAttentionBanner from "../../../../../mogul/components/LocationsNeedingAttentionBanner"
import FullCircleTableHeader from "./full-circle-header/FullCircleTableHeader"
import { FullCircleFilterObject } from "./full-circle-header/FullCircleFilterList"
import debounce from 'lodash.debounce'

const fullCircleSaveFilterToLocalStorageKey = 'full_circle_lead_filter_v1'

export function FullCircleLeadsTable() {
    const { currentUser } = useAuth();

    const isAdmin = !!currentUser?.is_admin;

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [errorLoading, setErrorLoading] = useState<string | null>(null);

    const [results, setResults] = useState<FullCircleLead[]>([]);
    const [totalResults, setTotalResults] = useState(0);

    const [currentFilterObject, setCurrentFilterObject] = useState<FullCircleFilterObject | null>(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Search states
    const [searchInput, setSearchInput] = useState('');
    const [personSearchFilter, setPersonSearchFilter] = useState('');
    const isFirstRender = useRef(true);

    const loadList = useCallback(
        async (forceFilter?: FullCircleFilterObject, page: number = 1) => {
            setErrorLoading(null);
            if (page === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            try {
                const limit = 5;
                const {
                    full_circle_leads: result,
                    total_full_circle_leads: totalResult,
                } = await getAllFullCircleLeads({
                    limit: 25,
                    page,
                    filter: forceFilter
                        ? {
                            org_ids: forceFilter?.selectedOrgIds || [],
                            preneed_options: forceFilter?.selectedPreneedOptions || [],
                            only_unsynced_with_360: forceFilter?.onlyUnSyncedWith360 || false,
                            include_leads_regardless_of_preneed_status: forceFilter?.includeLeadsRegardlessOfPreneedStatus || false,
                            start_unix_ts: forceFilter?.startingUnixTimestamp || undefined,
                            end_unix_ts: forceFilter?.endingUnixTimestamp || undefined,
                            person_search_filter: personSearchFilter || undefined,
                        }
                        : {
                            org_ids: currentFilterObject?.selectedOrgIds || [],
                            preneed_options: currentFilterObject?.selectedPreneedOptions || [],
                            only_unsynced_with_360: currentFilterObject?.onlyUnSyncedWith360 || false,
                            include_leads_regardless_of_preneed_status: currentFilterObject?.includeLeadsRegardlessOfPreneedStatus || false,
                            start_unix_ts: currentFilterObject?.startingUnixTimestamp || undefined,
                            end_unix_ts: currentFilterObject?.endingUnixTimestamp || undefined,
                            person_search_filter: personSearchFilter || undefined,
                        },
                });
                if (page === 1) {
                    setResults(result);
                } else {
                    setResults((prevResults) => [...prevResults, ...result]);
                }
                setTotalResults(totalResult);
                setHasMore(result.length === limit); // Assume there's more if we received a full page of results
            } catch (exception) {
                console.error(exception);
                const errorMessage =
                    exception instanceof APIError ? exception.message : 'Failed to load leads';
                setErrorLoading(errorMessage);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [currentFilterObject?.selectedOrgIds, currentFilterObject?.selectedPreneedOptions, currentFilterObject?.onlyUnSyncedWith360, currentFilterObject?.includeLeadsRegardlessOfPreneedStatus, currentFilterObject?.startingUnixTimestamp, currentFilterObject?.endingUnixTimestamp, personSearchFilter]
    );

    useEffect(() => {
        // Load from local storage
        try {
            const savedFilter = localStorage.getItem(fullCircleSaveFilterToLocalStorageKey);
            if (savedFilter) {
                const parsedFilter = JSON.parse(savedFilter);
                setCurrentFilterObject(parsedFilter);
                loadList(parsedFilter);
            } else {
                loadList();
            }
        } catch (error) {
            console.error('Failed to load saved filter', error);
        }
        // only load once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadMore = () => {
        if (hasMore && !loading) {
            setCurrentPage((prevPage) => prevPage + 1);
            loadList(currentFilterObject || undefined, currentPage + 1);
        }
    };

    // Create debounced search handler
    const debouncedSearch = useMemo(
        () => debounce((searchValue: string) => {
            setPersonSearchFilter(searchValue);
            setCurrentPage(1);
        }, 300),
        []
    );

    // Handle search input change
    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        debouncedSearch(value);
    };

    // Load list when search filter changes
    useEffect(() => {
        if (!isFirstRender.current) {
            loadList(currentFilterObject || undefined, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personSearchFilter]);

    // Mark first render as complete
    useEffect(() => {
        isFirstRender.current = false;
    }, []);

    return <div>
        <LocationsNeedingAttentionBanner inCategory="fca" />
        {loading ? <>
            <div className='p-10'>
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        </> : <>
            {errorLoading ? <>
                <div className='p-10'>
                    <div className="alert alert-danger" role="alert">
                        {errorLoading}
                    </div>
                    <div className='mt-5'>
                        <button className='btn btn-primary btn-lg' onClick={() => {
                            loadList()
                        }}>
                            Retry
                        </button>
                    </div>
                </div>
            </> : <>
                <FullCircleTableHeader
                    title={totalResults === 1 ? '1 Full-Circle Lead' : `${totalResults} Full-Circle Leads`}
                    subtitle={!isAdmin ? '' : ''}
                    filterLocalStorageKey={fullCircleSaveFilterToLocalStorageKey}
                    currentFilter={currentFilterObject}
                    onApplyFilter={(filterObject) => {
                        setCurrentFilterObject(filterObject)
                        loadList(filterObject)
                    }}
                    personSearchFilter={personSearchFilter}
                    searchBar={
                        <div className='d-flex align-items-center position-relative'>
                            <span className='svg-icon svg-icon-1 position-absolute ms-6'>
                                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                                    <path d='M21.7 18.9L18.6 15.8C17.9 16.9 16.9 17.9 15.8 18.6L18.9 21.7C19.3 22.1 19.9 22.1 20.3 21.7L21.7 20.3C22.1 19.9 22.1 19.3 21.7 18.9Z' fill='currentColor' />
                                    <path opacity='0.3' d='M11 20C6 20 2 16 2 11C2 6 6 2 11 2C16 2 20 6 20 11C20 16 16 20 11 20ZM11 4C7.1 4 4 7.1 4 11C4 14.9 7.1 18 11 18C14.9 18 18 14.9 18 11C18 7.1 14.9 4 11 4ZM8 11C8 9.3 9.3 8 11 8C11.6 8 12 7.6 12 7C12 6.4 11.6 6 11 6C8.2 6 6 8.2 6 11C6 11.6 6.4 12 7 12C7.6 12 8 11.6 8 11Z' fill='currentColor' />
                                </svg>
                            </span>
                            <input
                                type='text'
                                className='form-control form-control-solid w-300px ps-15'
                                placeholder='Search by name, organization, phone, or email...'
                                value={searchInput}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            {searchInput && (
                                <button
                                    className='btn btn-flush btn-active-color-primary position-absolute end-0'
                                    onClick={() => handleSearchChange('')}
                                    style={{ right: '10px' }}
                                >
                                    <span className='svg-icon svg-icon-2'>
                                        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                                            <path opacity='0.3' d='M6 19.7C5.7 19.7 5.5 19.6 5.3 19.4C4.9 19 4.9 18.4 5.3 18L18 5.3C18.4 4.9 19 4.9 19.4 5.3C19.8 5.7 19.8 6.29999 19.4 6.69999L6.7 19.4C6.5 19.6 6.3 19.7 6 19.7Z' fill='currentColor' />
                                            <path d='M18.8 19.7C18.5 19.7 18.3 19.6 18.1 19.4L5.40001 6.69999C5.00001 6.29999 5.00001 5.7 5.40001 5.3C5.80001 4.9 6.40001 4.9 6.80001 5.3L19.5 18C19.9 18.4 19.9 19 19.5 19.4C19.3 19.6 19 19.7 18.8 19.7Z' fill='currentColor' />
                                        </svg>
                                    </span>
                                </button>
                            )}
                        </div>
                    }
                />
                <div className='px-10'>
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        {/* begin::Table head */}
                        <thead>
                            <tr className='fw-bold text-muted'>
                                <th className='min-w-150px'>Last Updated</th>
                                <th className='min-w-150px'>
                                    Funeral Home
                                </th>
                                <th>Deceased</th>
                                <th>Lead Name</th>
                                <th className='min-w-150px'>Contact</th>
                                <th>Relationship</th>
                                <th>Pre-Need?</th>
                                <th>Preneed Notes</th>
                                <th className='min-w-150px'></th>
                            </tr>
                        </thead>
                        {/* end::Table head */}
                        {/* begin::Table body */}
                        <tbody>
                            {results.map((item) => {

                                const funeralHomeLabel = `${item.deal.pipedrive_account_organization ? item.deal.pipedrive_account_organization.org_name : 'Unknown Funeral Home'}`

                                return <tr key={`item-${item.deal.unique_identifier}-${item.person.unique_identifier}`} data-unique-identifier={`${item.deal.unique_identifier}-${item.person.unique_identifier}`}>
                                    <td>
                                        {formatLeadItemDateBetter(item.deal.last_updated_from_pipedrive_at)}
                                    </td>
                                    <td>
                                        {item.deal.pipedrive_account_organization ? <Link to={`/fca/manage-org/${item.deal.pipedrive_account_organization.unique_identifier}`}>
                                            {funeralHomeLabel}
                                        </Link> : funeralHomeLabel}
                                        {personSearchFilter && item.deal.pipedrive_account_organization?.org_name?.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                            <span className='badge badge-light-info ms-2'>Organization Match</span>
                                        )}
                                    </td>
                                    <td>
                                        {/* Deceased Name, 29280234f3abc430ca561212b5dffce4d4bde41a */}
                                        {item.deal.deceased_full_name}
                                        {personSearchFilter && item.deal.deceased_full_name?.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                            <span className='badge badge-light-info ms-2'>Deceased Match</span>
                                        )}
                                    </td>
                                    <td>
                                        {item.person.lead_full_name}
                                        {personSearchFilter && item.person.lead_full_name?.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                            <span className='badge badge-light-info ms-2'>Lead Name Match</span>
                                        )}
                                    </td>
                                    <td>
                                        <div>
                                            {item.person.lead_phone_numbers.map(x => x.value).join(', ')}
                                        </div>
                                        <div>
                                            {item.person.lead_email_addresses.map(x => x.value).join(', ')}
                                        </div>
                                        {personSearchFilter && (
                                            (item.person.lead_phone_numbers.some(phone => phone.value?.toLowerCase().includes(personSearchFilter.toLowerCase())) && (
                                                <span className='badge badge-light-info ms-2'>Phone Match</span>
                                            )) ||
                                            (item.person.lead_email_addresses.some(email => email.value?.toLowerCase().includes(personSearchFilter.toLowerCase())) && (
                                                <span className='badge badge-light-info ms-2'>Email Match</span>
                                            ))
                                        )}
                                    </td>
                                    <td>
                                        {/* Relationship, 6b3f919f6bbf6be25352f07bfc0f2230ca34c87b */}
                                        {item.person.relationship_to_deceased}
                                    </td>
                                    <td>
                                        {/* Pre-Need?, 7948db556604a4bb9c39dacb28ba47352ec71b8b */}
                                        {item.deal.preneed_interest_level}
                                    </td>
                                    <td>
                                        {/* Preneed Lead Notes, 900d26a002b5b95b5d8d4c1628373416989d3541 */}
                                        {item.deal.preneed_notes}
                                        {item.deal.date_serviced && (
                                            <div className="mt-2">
                                                <small className="text-muted">Date Serviced: {item.deal.date_serviced}</small>
                                            </div>
                                        )}
                                        <br />
                                        {item.deal.lost_reason}
                                    </td>
                                    <td>
                                        {item.deal.ghl_contact?.ghl_contact_id ? <div>
                                            <div>
                                                <a href={
                                                    `https://app.premier360platform.com/v2/location/${item.deal.ghl_contact.ghl_location_id}/contacts/detail/${item.deal.ghl_contact.ghl_contact_id}`
                                                } target='_blank' rel='noreferrer'>
                                                    View in FRM
                                                </a>
                                            </div>
                                            <div className='mt-2'>
                                                {/* Sync to 360 */}
                                                <SyncLeadButton uniqueIdentifier={item.deal.unique_identifier} isSecondary />
                                            </div>
                                        </div> : <>
                                            <div className='mt-2'>
                                                {/* Sync to 360 */}
                                                <SyncLeadButton uniqueIdentifier={item.deal.unique_identifier} />
                                            </div>
                                        </>}
                                    </td>
                                </tr>
                            })}
                            {!loading && hasMore && (
                                <tr>
                                    <td colSpan={8}>
                                        <button
                                            disabled={loadingMore}
                                            onClick={loadMore}
                                            className='btn btn-primary'
                                        >
                                            {loadingMore ? 'Loading...' : 'Load More'}
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {/* end::Table body */}
                    </table>
                </div>
            </>}
        </>}
    </div>

}